import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent, Subscription } from 'rxjs';
import { MonacoEditorLoaderService } from './monaco-editor-loader.service';

export interface MonacoEditorChangeTouchDelegate {
  (val?: string): void;
}

@Component({
    selector: 'app-monaco-editor',
    template: '<div class="datex-monaco-container"><div class="datex-monaco-inner-container" #editorContainer></div></div>',
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MonacoEditorComponent),
            multi: true
        }],
    standalone: false
})
export class MonacoEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('editorContainer', { static: true }) _editorContainer: ElementRef;

  @Input()
  set options(options: monaco.editor.IStandaloneEditorConstructionOptions) {
    this._options = options;
    if (this._editor) {
      this._editor.dispose();
      this.initMonaco();
    }
  }
  get options(): monaco.editor.IStandaloneEditorConstructionOptions {
    return this._options;
  }

  // Monaco editor themes for light mode and dark mode
  @Input()
  lightTheme = 'vs';
  @Input()
  darkTheme = 'vs-dark';

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onInit = new EventEmitter<monaco.editor.IStandaloneCodeEditor>();

  private _value = '';
  private _disabled = false;
  protected _editor: monaco.editor.IStandaloneCodeEditor;
  protected _options: monaco.editor.IStandaloneEditorConstructionOptions;
  protected _windowResizeSubscription: Subscription;
  protected _monacoLoadedSubscription: Subscription;
  /** Notifies Monaco when its container element resizes — flex/grid layout
   *  shifts that happen *after* monaco.editor.create() never fire a window
   *  resize event, leaving Monaco frozen at whatever (often very wrong) size
   *  it measured at creation. Without this, the editor renders at runaway
   *  heights (observed 280–323k px) which spams requestAnimationFrame and
   *  mis-positions overlay widgets. */
  protected _containerResizeObserver: ResizeObserver;

  onChange: MonacoEditorChangeTouchDelegate = () => void 0;
  onTouched: MonacoEditorChangeTouchDelegate = () => void 0;

  constructor(private zone: NgZone, private monacoEditorLoaderService: MonacoEditorLoaderService) {
  }

  ngAfterViewInit(): void {
    this._monacoLoadedSubscription = this.monacoEditorLoaderService.loaded.subscribe(loaded => {
      if (loaded) {
        this.initMonaco();
      }
    });
  }

  ngOnDestroy() {
    if (this._windowResizeSubscription) {
      this._windowResizeSubscription.unsubscribe();
    }
    if (this._monacoLoadedSubscription) {
      this._monacoLoadedSubscription.unsubscribe();
    }
    if (this._containerResizeObserver) {
      this._containerResizeObserver.disconnect();
      this._containerResizeObserver = undefined;
    }
    if (this._editor) {
      this._editor.dispose();
      this._editor = undefined;
    }
  }

  private initMonaco(): void {
    let hasModel;
    if (this._options) {
      hasModel = !!this._options.model;

      if (hasModel) {
        const model = monaco.editor.getModel(this._options.model.uri || monaco.Uri.parse(''));
        if (model) {
          this._options.model = model;
          this._options.model.setValue(this._value);
        } else {
          this._options.model = monaco.editor.createModel(this._options.value, this._options.language, this._options.model.uri);
        }
      }
    }

    // run outside angular, since the editor creation results in creating a lot of event
    // listeners which could trigger the angular CD
    this.zone.runOutsideAngular(() => {
      this._editor = monaco.editor.create(this._editorContainer.nativeElement, this._options);
    });

    if (!hasModel) {
      this._editor.setValue(this._value);
    }

    this._editor.onDidChangeModelContent(() => {
      this.zone.run(() => {
        const value = this._editor.getValue();
        this._value = value;
        this.onChange(value);
      });
    });

    this._editor.onDidBlurEditorWidget(() => {
      this.zone.run(() => {
        this.onTouched();
      });
    });

    // refresh layout on resize event.
    if (this._windowResizeSubscription) {
      this._windowResizeSubscription.unsubscribe();
    }
    this._windowResizeSubscription = fromEvent(window, 'resize').subscribe(() => this._editor.layout());

    // Observe the container so Monaco relayouts whenever its parent flex/grid
    // ancestors shift — required because monaco.editor.create() snapshots the
    // container size at creation time and Monaco has no built-in container
    // observer unless `automaticLayout: true` is set on every options object.
    // Layout calls run outside Angular to avoid spurious change-detection
    // ticks; Monaco's own scheduling debounces internally.
    if (this._containerResizeObserver) {
      this._containerResizeObserver.disconnect();
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        this._containerResizeObserver = new ResizeObserver(() => {
          this._editor?.layout();
        });
        this._containerResizeObserver.observe(this._editorContainer.nativeElement);
      });
    }
    // Also trigger an explicit layout on the next frame to catch the case
    // where the container's final size is only known after the current
    // Angular tick finishes painting the surrounding layout.
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this._editor?.layout());
    });
    // set dark mode
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.zone.run(() => {
        monaco.editor.setTheme(this.darkTheme);
      });
    }

    // set light/dark mode when switching preference
    const darkModePreference = window.matchMedia('(prefers-color-scheme: dark)');
    darkModePreference.addEventListener( 'change', (e) => {
      if (e.matches) {
        monaco.editor.setTheme(this.darkTheme);
      }
      else {
        monaco.editor.setTheme(this.lightTheme);
      }
    });
    this.onInit.emit(this._editor);
  }

  writeValue(value: string): void {
    if (this._value !== value) {
      this._value = value || '';
      // Fix for value change while dispose in process.
      setTimeout(() => {
        if (this._editor) {
          this._editor.setValue(this._value);
        }
      });
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabled = isDisabled;
    if (this._editor) {
      this._editor.updateOptions({ readOnly: this._disabled });
    }
  }

  registerOnChange(fn: MonacoEditorChangeTouchDelegate): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: MonacoEditorChangeTouchDelegate): void {
    this.onTouched = fn;
  }
}
